import { PASSAGE_QUOTE_MAX } from "@esi/shared";
import { useCallback, useEffect, useState } from "react";

/** Instructions one tray holds (spec `project-wiki` "Instructions to the agent on the preview"). */
export const INSTRUCTIONS_MAX = 20;
/** An instruction's length, in characters. */
export const INSTRUCTION_MAX = 1000;
/** A quoted passage's length, in characters (D11). */
export const QUOTE_MAX = PASSAGE_QUOTE_MAX;

/** One instruction: the passage it points at, where it sits, and what the agent should do. */
export interface Instruction {
  id: string;
  quote: string;
  page: string;
  /** The enclosing heading's text, or null for the page's opening. */
  section: string | null;
  text: string;
}

const HEADING = /^Instructions on the draft "(.*)":$/;

/** One line, no blank runs: a quote or an instruction never breaks the message's blocks. */
const tidy = (text: string) =>
  text
    .replace(/\r\n?/g, "\n")
    .replace(/\n\s*\n+/g, "\n")
    .trim();

/**
 * The plain Markdown message of design D3: a heading line naming the draft, then per instruction
 * the quoted passage, a "— Page › Section" line and the instruction. It is the user's own turn;
 * an older SPA shows it as text, which still reads.
 */
export function instructionsMessage(draftTitle: string, list: readonly Instruction[]): string {
  const blocks = list.map((i) => {
    const quote = tidy(i.quote)
      .split("\n")
      .map((line) => `> ${line}`)
      .join("\n");
    const where = [i.page, ...(i.section ? [i.section] : [])].join(" › ");
    // An instruction line never reads as a quote.
    const text = tidy(i.text).replace(/^>/gm, "\\>");
    return `${quote}\n> — ${tidy(where).replace(/\n/g, " ")}\n${text}`;
  });
  return [`Instructions on the draft "${draftTitle.replace(/\n/g, " ")}":`, ...blocks].join("\n\n");
}

/** The instructions a message of `instructionsMessage` carries, or null for any other text. */
export function parseInstructions(
  text: string,
): { title: string; items: Omit<Instruction, "id">[] } | null {
  const [head, ...blocks] = text.replace(/\r\n?/g, "\n").trim().split(/\n\n+/);
  const title = HEADING.exec(head ?? "")?.[1];
  if (title === undefined || !blocks.length) {
    return null;
  }
  const items: Omit<Instruction, "id">[] = [];
  for (const block of blocks) {
    const lines = block.split("\n");
    const quoted = lines.filter((l) => l.startsWith(">"));
    const rest = lines.filter((l) => !l.startsWith(">"));
    const where = quoted.at(-1)?.replace(/^>\s?/, "");
    if (!where?.startsWith("— ") || quoted.length < 2 || !rest.length) {
      return null;
    }
    const [page = "", ...section] = where.slice(2).split(" › ");
    items.push({
      quote: quoted
        .slice(0, -1)
        .map((l) => l.replace(/^>\s?/, ""))
        .join("\n"),
      page,
      section: section.length ? section.join(" › ") : null,
      text: rest.join("\n"),
    });
  }
  return { title, items };
}

const trayKey = (changeSetId: string) => `esi.wiki.instructions.${changeSetId}`;
const sendKey = (conversationId: string) => `esi.wiki.instructions.send.${conversationId}`;
const SEND_EVENT = "esi:wiki-instructions";

function read<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function write(key: string, value: unknown) {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Storage blocked: the tray lives in component state only.
  }
}

/**
 * The tray of one change set (D3): component state mirrored in `sessionStorage`, so closing the
 * panel or reloading keeps the instructions; nothing leaves the browser before Send.
 */
export function useInstructionTray(changeSetId: string) {
  const [items, setItems] = useState<Instruction[]>(
    () => read<Instruction[]>(trayKey(changeSetId))?.slice(0, INSTRUCTIONS_MAX) ?? [],
  );
  useEffect(() => {
    setItems(read<Instruction[]>(trayKey(changeSetId))?.slice(0, INSTRUCTIONS_MAX) ?? []);
  }, [changeSetId]);
  const save = useCallback(
    (next: Instruction[]) => {
      setItems(next);
      write(trayKey(changeSetId), next.length ? next : null);
    },
    [changeSetId],
  );
  return {
    items,
    full: items.length >= INSTRUCTIONS_MAX,
    add: (item: Omit<Instruction, "id">) =>
      items.length < INSTRUCTIONS_MAX &&
      save([
        ...items,
        {
          ...item,
          quote: item.quote.slice(0, QUOTE_MAX),
          text: item.text.slice(0, INSTRUCTION_MAX),
          id: crypto.randomUUID(),
        },
      ]),
    remove: (id: string) => save(items.filter((i) => i.id !== id)),
    clear: () => save([]),
  };
}

/** Messages handed over in this page's memory when `sessionStorage` is blocked or full. */
const memory = new Map<string, string[]>();

/**
 * Hands the composed message to the conversation's thread, which sends it as its next turn: the
 * panel and the thread are siblings, and the full review page navigates to the conversation
 * first (an in-app navigation: memory survives it). Kept per conversation in `sessionStorage`
 * (memory when storage fails), in order, until the thread takes each one: a batch sent while a
 * turn runs waits for it and is never overwritten by the next.
 */
export function queueInstructions(conversationId: string, text: string) {
  const queued = read<string[]>(sendKey(conversationId));
  const next = [...(Array.isArray(queued) ? queued : []), text];
  write(sendKey(conversationId), next);
  if (read<string[]>(sendKey(conversationId))?.length !== next.length) {
    memory.set(conversationId, [...(memory.get(conversationId) ?? []), text]);
  }
  window.dispatchEvent(new CustomEvent(SEND_EVENT, { detail: conversationId }));
}

/** The thread's side: take the oldest queued message for `conversationId` whenever `ready`. */
export function useQueuedInstructions(
  conversationId: string | undefined,
  ready: boolean,
  send: (text: string) => void,
) {
  useEffect(() => {
    if (!conversationId || !ready) {
      return;
    }
    const take = () => {
      const queued = read<string[]>(sendKey(conversationId));
      const [next, ...rest] = Array.isArray(queued) ? queued : [];
      // One turn at a time: the next batch goes once this one ends (`ready` again).
      if (typeof next === "string" && next) {
        write(sendKey(conversationId), rest.length ? rest : null);
        send(next);
        return;
      }
      const [held, ...later] = memory.get(conversationId) ?? [];
      if (held) {
        memory.set(conversationId, later);
        send(held);
      }
    };
    take();
    const onEvent = (e: Event) => {
      if ((e as CustomEvent<string>).detail === conversationId) {
        take();
      }
    };
    window.addEventListener(SEND_EVENT, onEvent);
    return () => window.removeEventListener(SEND_EVENT, onEvent);
  }, [conversationId, ready, send]);
}
